<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpTextField from '@/components/KpTextField.vue';
import { Benutzer } from '@/models/Benutzer';
import { createValidator, createValidators, validateEmail, validateNotEmpty } from '@/helpers/model_validators';
import KpPasswordField from '@/components/KpPasswordField.vue';
import Divider from 'primevue/divider';
import { formValidators } from '@/helpers/forms';
import { setSession, useAuthUser, useSession } from '@/helpers/globals';
import { useConfirm } from 'primevue';
import { useI18n } from 'vue-i18n';
import { logout, resetTotpKey } from '@/helpers/auth';
import ConfirmDialog from 'primevue/confirmdialog';
import { appUrl } from '@/helpers/api';

const user = useAuthUser();
const record: Ref<Benutzer | null> = ref(null);
const router = useRouter();
const toast = useToast();
const saving = ref(false);
const loading = ref(false);

const state = reactive({
    passwort: '',
    passwort2: '',
});

const { allValid, validators } = createValidators({
    username: createValidator(
        computed(() => user?.username),
        [validateNotEmpty],
    ),
    email: createValidator(
        computed(() => user?.email || null),
        [validateNotEmpty, validateEmail],
    ),
    pw_complexity: createValidator(
        computed(() => state.passwort),
        [formValidators.passwordComplexity],
    ),
    same_pw: createValidator(
        computed(() => state.passwort === state.passwort2),
        [(val: boolean) => (val ? true : getI18n().t('keine_uebereinstimmung'))],
    ),
});

onBeforeRouteLeave(() => {
    return reallyCancel(user);
});

onMounted(() => {
    loadRecord();
});

async function loadRecord() {
    if (user?.id && user.id > 0) {
        try {
            const rec = await Benutzer.loadById(user.id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record.value === null) {
        return;
    }
    saving.value = true;
    try {
        if (state.passwort.length > 0 && state.passwort === state.passwort2) {
            record.value.set('passwort', state.passwort);
        }
        await record.value.save();
        record.value.passwort = '';
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        setSession({
            user: record.value,
            role: useSession().role,
            csrfToken: useSession().csrfToken,
            authLevel: useSession().authLevel,
        });
        router.push({ name: 'home' });
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record.value)) {
        user?.rollback();
        router.push({ name: 'home' });
    }
}

const confirm = useConfirm();
const { t } = useI18n();
function revokeTotp() {
    console.log('foo');
    confirm.require({
        message: t('2fa_zuruecksetzen_warnung'),
        header: t('achtung'),
        rejectProps: {
            label: t('nein'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('ja'),
        },
        accept: async () => {
            try {
                loading.value = true;
                await resetTotpKey(user?.id || -1);
                await logout();
                window.location.href = appUrl('/login');
            } catch (e) {
                errToToast(e, toast);
            } finally {
                loading.value = false;
            }
        },
        reject: () => (loading.value = false),
    });
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2><i class="fas fa-user"></i> {{ $t('benutzer_profil') }}</h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpTextField
                        class="col-6"
                        fluid
                        :label="$t('benutzername')"
                        placeholder="abc123"
                        v-model="record.username"
                        :invalid="!validators.username.isValid.value"
                        :error="validators.username.msg.value"
                    />
                    <KpTextField
                        class="col-6"
                        fluid
                        :label="$t('email')"
                        placeholder="abc123@domain.com"
                        v-model="record.email"
                        :invalid="!validators.email.isValid.value"
                        :error="validators.email.msg.value"
                    />
                </div>

                <div class="formgrid grid align-items-start">
                    <KpTextField
                        class="col-6"
                        fluid
                        :label="$t('nachname')"
                        placeholder="Muster"
                        v-model="record.nachname"
                    />
                    <KpTextField
                        class="col-6"
                        fluid
                        :label="$t('vorname')"
                        placeholder="Anna"
                        v-model="record.vorname"
                    />
                </div>

                <div class="formgrid grid align-items-start">
                    <KpPasswordField
                        class="col-6"
                        fluid
                        :label="$t('passwort')"
                        placeholder="z.B. xYz91D(1/"
                        show-error-placeholder
                        v-model="state.passwort"
                        :invalid="!validators.pw_complexity.isValid.value"
                        :error="validators.pw_complexity.msg.value"
                    >
                        <template #footer>
                            <Divider></Divider>
                            <p>Mind. 8 Buchstaben</p>
                            <p>Mind. 1 Grossbuchstabe</p>
                            <p>Mind. 1 Kleinbuchstabe</p>
                            <p>Mind. 1 Zahl</p>
                        </template>
                    </KpPasswordField>
                    <KpPasswordField
                        class="col-6"
                        fluid
                        :label="$t('passwort_wiederholen')"
                        :feedback="false"
                        :invalid="!validators.same_pw.isValid.value"
                        :error="validators.same_pw.msg.value"
                        show-error-placeholder
                        v-model="state.passwort2"
                    />
                </div>
                <div>
                    <KpButton class="mt-1rem" :label="$t('2fa_zuruecksetzen')" @click="revokeTotp" :loading="loading"> </KpButton>
                </div>
            </template>
        </Card>
        <ConfirmDialog
            ><template #message="slotProps">
                <div v-html="slotProps.message.message"></div>
            </template>
        </ConfirmDialog>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
